/**
 * ARAM Platform - Legacy fetch() authorization shim
 * Core Desarrollos S.A.S - Copyright 2024-2026
 *
 * Several older pages call the browser `fetch()` API directly instead of the
 * shared `apiClient` (axios) instance, so they never attached the JWT bearer
 * token. That was harmless while some backend routes allowed anonymous
 * access, but it breaks now that the API denies unauthenticated requests by
 * default. This shim patches `window.fetch` once at boot so every same-origin
 * `/api/` request carries the current access token, without having to rewrite
 * every legacy call site.
 */

import { useAuthStore } from '@/stores/authStore'

const originalFetch = window.fetch.bind(window)

window.fetch = (input: RequestInfo | URL, init: RequestInit = {}) => {
  const url = typeof input === 'string' ? input : input instanceof Request ? input.url : String(input)
  const isApiRequest = url.startsWith('/api/') || url.includes('/api/v1/')

  if (!isApiRequest) return originalFetch(input, init)

  const headers = new Headers(init.headers ?? (input instanceof Request ? input.headers : undefined))
  if (!headers.has('Authorization')) {
    const token = useAuthStore.getState().accessToken
    if (token) headers.set('Authorization', `Bearer ${token}`)
  }

  return originalFetch(input, { ...init, headers })
}
